import { useMemo } from "react";
import { AlertTriangle, DollarSign, Truck, TrendingUp } from "lucide-react";
import { money } from "@/lib/format";

const monthStart = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); };
const weekStart = () => { const n = new Date(); const d = new Date(n.getFullYear(), n.getMonth(), n.getDate()); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };
const total = (ls) => ls.reduce((s, l) => s + (Number(l.billable_total ?? l.rate) || 0), 0);

export const CARD_FILTERS = {
  active: (l) => l.status === "New" || l.status === "In Progress",
  missing_pod: (l) => l.status === "Delivered" && !l.has_pod,
  unbilled: (l) => l.status === "Delivered" && !l.invoice_id,
  revenue_month: (l) => new Date(l.created_at) >= monthStart(),
  revenue_all: () => true,
};

const Card = ({ id, on, onClick, tint, ring, label, icon: Icon, value, sub, children }) => (
  <div role="button" tabIndex={0} aria-pressed={on} onClick={onClick} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    data-testid={`stat-${id}`}
    className={`text-left rounded-xl border p-4 cursor-pointer transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 ${tint} ${on ? `ring-2 ring-offset-2 ${ring} shadow-md` : ""}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs font-medium uppercase tracking-wider opacity-80 truncate">{label}</div>
      <Icon className="w-4 h-4 opacity-80 shrink-0" />
    </div>
    <div className="mt-2 text-2xl md:text-3xl font-bold tabular-nums truncate" style={{ fontFamily: "Outfit" }} data-testid={`stat-${id}-value`}>{value}</div>
    <div className="mt-1 text-[11px] opacity-75 truncate" data-testid={`stat-${id}-sub`}>{on ? "Showing these \u00b7 tap to clear" : sub}</div>
    {children}
  </div>
);

export const StatCards = ({ loads, active, onToggle, allTime, setAllTime }) => {
  const t = useMemo(() => {
    const by = (k) => loads.filter(CARD_FILTERS[k]);
    const unbilled = by("unbilled");
    return {
      active: by("active").length, pod: by("missing_pod").length, unbilled: total(unbilled), unbilledCount: unbilled.length,
      revenueMonth: total(by("revenue_month")), revenueAll: total(loads),
      week: loads.filter((l) => new Date(l.created_at) >= weekStart()).length, month: by("revenue_month").length,
    };
  }, [loads]);
  const revKey = allTime ? "revenue_all" : "revenue_month";
  const revOn = active === "revenue_month" || active === "revenue_all";
  const tap = (k) => () => onToggle(active === k ? null : k);
  const monthName = new Date().toLocaleDateString("en-US", { month: "long" });
  const setPeriod = (v) => (e) => { e.stopPropagation(); setAllTime(v); if (revOn) onToggle(v ? "revenue_all" : "revenue_month"); };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card id="active" on={active === "active"} onClick={tap("active")} icon={Truck} label="Active" value={t.active} sub="New + in progress"
          tint="text-blue-700 bg-blue-50 border-blue-200" ring="ring-blue-500" />
        <Card id="missing-pod" on={active === "missing_pod"} onClick={tap("missing_pod")} icon={AlertTriangle} label="Missing POD" value={t.pod} sub="Delivered, no POD"
          tint="text-amber-700 bg-amber-50 border-amber-200" ring="ring-amber-500" />
        <Card id="unbilled" on={active === "unbilled"} onClick={tap("unbilled")} icon={DollarSign} label="Unbilled" value={money(t.unbilled)} sub={`${t.unbilledCount} delivered, not invoiced`}
          tint="text-rose-700 bg-rose-50 border-rose-200" ring="ring-rose-500" />
        <Card id="revenue" on={revOn} onClick={tap(revKey)} icon={TrendingUp} label={allTime ? "Revenue · all" : `Revenue · ${monthName.slice(0, 3)}`} value={money(allTime ? t.revenueAll : t.revenueMonth)}
          sub="Freight + billable extras" tint="text-white bg-[#002FA7] border-[#002FA7]" ring="ring-[#002FA7]">
          <div className="mt-2 inline-flex rounded-full bg-white/15 p-0.5 text-[11px]" role="group" aria-label="Revenue period">
            {[["Month", "This month", false], ["All", "All time", true]].map(([short, label, v]) => (
              <button key={label} type="button" onClick={setPeriod(v)} aria-pressed={allTime === v} aria-label={label}
                className={`px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${allTime === v ? "bg-white text-slate-900 font-semibold" : "text-blue-100 hover:text-white"}`}
                data-testid={`revenue-toggle-${v ? "all" : "month"}`}><span className="sm:hidden">{short}</span><span className="hidden sm:inline">{label}</span></button>
            ))}
          </div>
        </Card>
      </div>
      <p className="text-xs text-slate-500" data-testid="dashboard-load-counts">{t.week} load{t.week === 1 ? "" : "s"} this week{" \u00b7 "}{t.month} this month</p>
    </div>
  );
};
